import React from 'react';
import { format, parseISO, addDays } from 'date-fns';
import StatusPill from '../../../../components/StatusPill';
import { CheckIcon } from '../../../../components/IconButton/icons';
import { BATCH_STATUS_TONE, TimesheetBatch } from '../../config';
import { WeekStats } from '../../timesheetDerive';
import { formatHoursShort } from '../../../../utils/timesheetTime';
import './timesheet-header.scss';

interface TimesheetHeaderProps {
  batch: TimesheetBatch;
  stats: WeekStats;
  /** Standard hours per day; the week target is five of these. */
  standardHours: number;
  /** Who is looking at it: shown as the strip's title on All Timesheets. */
  employeeName?: string;
  /** Right-hand controls: submit, reopen, approve, unapprove. */
  actions?: React.ReactNode;
}

type ProgressState = 'is-empty' | 'is-partial' | 'is-done';

/** The ring beside a figure: empty, half (under way) or filled (met). */
const progressOf = (value: number, target: number): ProgressState =>
  target > 0 && value >= target ? 'is-done' : value > 0 ? 'is-partial' : 'is-empty';

/**
 * The identity strip at the top of a timesheet week: which week, its status
 * and who approved it on the head row, then the week's figures as progress
 * rows under it -- the same card the project modal uses for PEP
 * completeness, so a week reads the way a job does.
 */
const TimesheetHeader: React.FC<TimesheetHeaderProps> = ({ batch, stats, standardHours, employeeName, actions }) => {
  const end = parseISO(batch.week_ending_date);
  const start = addDays(end, -6);
  const target = standardHours * 5;
  const remaining = Math.max(0, target - stats.total);

  const rows: { key: string; label: string; value: React.ReactNode; state: ProgressState; note?: string }[] = [
    {
      key: 'week',
      label: 'Week hours',
      value: <>{formatHoursShort(stats.total)}<span className="week-progress__of"> / {formatHoursShort(target)}</span></>,
      state: progressOf(stats.total, target),
      note: stats.total >= target ? 'Target met' : `${formatHoursShort(remaining)} to target`,
    },
    { key: 'project', label: 'Project hours', value: formatHoursShort(stats.project), state: stats.project > 0 ? 'is-partial' : 'is-empty' },
    { key: 'leave', label: 'Leave hours', value: formatHoursShort(stats.leave), state: stats.leave > 0 ? 'is-partial' : 'is-empty' },
    {
      key: 'days',
      label: 'Days logged',
      value: <>{stats.daysLogged}<span className="week-progress__of"> / 5</span></>,
      state: progressOf(stats.daysLogged, 5),
    },
  ];

  return (
    <div className="timesheet-header">
      <div className="identity-strip">
        <div className="identity-strip__head">
        <div className="identity-strip__main">
          <span className="identity-strip__number">
            {format(start, 'EEE d MMM').toUpperCase()} – {format(end, 'EEE d MMM yyyy').toUpperCase()}
          </span>
          {employeeName && <span className="identity-strip__title">{employeeName}</span>}
          {batch.status !== 'Draft' && (
          <span className="identity-strip__line">
            {batch.status === 'Submitted' && batch.submitted_at && (
              <span>Submitted {format(new Date(batch.submitted_at), 'd MMM yyyy, h:mm a')}</span>
            )}
            {batch.status === 'Approved' && (
              <>
                {batch.submitted_at && <span>Submitted {format(new Date(batch.submitted_at), 'd MMM yyyy')}</span>}
                {batch.submitted_at && <span className="sep">·</span>}
                <span>
                  Approved
                  {batch.approver && ` by ${batch.approver.first_name} ${batch.approver.last_name}`}
                  {batch.approved_at && ` on ${format(new Date(batch.approved_at), 'd MMM yyyy')}`}
                </span>
              </>
            )}
          </span>
          )}
        </div>
        <div className="identity-strip__side">
          <StatusPill label={batch.status} tone={BATCH_STATUS_TONE[batch.status]} />
          {actions}
        </div>
        </div>

        <div className="week-progress" role="list" aria-label="Week figures">
          {rows.map((row) => (
            <div key={row.key} role="listitem" className={`week-progress__row ${row.state}`}>
              <span className="week-progress__mark" aria-hidden="true">
                {row.state === 'is-done' && <CheckIcon size={10} />}
              </span>
              <span className="week-progress__label">{row.label}</span>
              <span className="week-progress__count">{row.value}</span>
              {row.note && <span className="week-progress__note">{row.note}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimesheetHeader;
